import {
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useMutation,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query';
import { queryClient, websiteContentApi } from '~/apis';
import {
  MonitoringFilterProtocolResponse,
  MonitoringWebsiteContentWebsiteContentCommonProtocolsGetCommentUserTypeFilterRequest,
  MonitoringWebsiteContentWebsiteContentCommonProtocolsGetCountRequest,
  MonitoringWebsiteContentWebsiteContentCommonProtocolsGetCountResponse,
  MonitoringWebsiteContentWebsiteContentCommonProtocolsGetCountryFilterRequest,
  MonitoringWebsiteContentWebsiteContentCommonProtocolsGetCustomTagsFilterRequest,
  MonitoringWebsiteContentWebsiteContentCommonProtocolsGetDomainFilterRequest,
  MonitoringWebsiteContentWebsiteContentCommonProtocolsGetTpProgressFilterRequest,
  MonitoringWebsiteContentWebsiteContentCommonProtocolsGetWebsiteDetailRequest,
  MonitoringWebsiteContentWebsiteContentCommonProtocolsGetWebsiteDetailResponse,
  MonitoringWebsiteContentWebsiteContentCommonProtocolsGetWebsiteListRequest,
  MonitoringWebsiteContentWebsiteContentCommonProtocolsGetWebsiteListResponse,
} from '~/swagger/data-contracts';
import { websiteContentQueryKeys } from './queryKeys';

export const useDomainFilterQuery = <TData>(
  request: MonitoringWebsiteContentWebsiteContentCommonProtocolsGetDomainFilterRequest,
  options: UseInfiniteQueryOptions<MonitoringFilterProtocolResponse, unknown, TData>
) => {
  return useInfiniteQuery<MonitoringFilterProtocolResponse, unknown, TData>(
    websiteContentQueryKeys.filter.domain(request),
    async ({ pageParam }) => {
      const { data } = await websiteContentApi.contentsFilterDomainCreate({
        ...request,
        search_after: pageParam || [],
      });
      return data;
    },
    {
      ...options,
      getNextPageParam: (lastPage) => {
        return lastPage.search_after;
      },
    }
  );
};

export const useCustomTagsFilterQuery = <TData>(
  request: MonitoringWebsiteContentWebsiteContentCommonProtocolsGetCustomTagsFilterRequest,
  options: UseQueryOptions<MonitoringFilterProtocolResponse, unknown, TData>
) => {
  return useQuery<MonitoringFilterProtocolResponse, unknown, TData>({
    queryKey: websiteContentQueryKeys.filter.customTag(request),
    queryFn: async () => {
      const { data } = await websiteContentApi.contentsFilterCustomTagsCreate(request);
      return data;
    },
    ...options,
  });
};

export const useCountryFilterQuery = <TData>(
  request: MonitoringWebsiteContentWebsiteContentCommonProtocolsGetCountryFilterRequest,
  options: UseQueryOptions<MonitoringFilterProtocolResponse, unknown, TData>
) => {
  return useQuery<MonitoringFilterProtocolResponse, unknown, TData>({
    queryKey: websiteContentQueryKeys.filter.country(request),
    queryFn: async () => {
      const { data } = await websiteContentApi.contentsFilterCountryCreate(request);
      return data;
    },
    ...options,
  });
};

export const useTestPurchaseProgressFilterQuery = <TData>(
  request: MonitoringWebsiteContentWebsiteContentCommonProtocolsGetTpProgressFilterRequest,
  options: UseQueryOptions<MonitoringFilterProtocolResponse, unknown, TData>
) => {
  return useQuery<MonitoringFilterProtocolResponse, unknown, TData>({
    queryKey: websiteContentQueryKeys.filter.tpProgress(request),
    queryFn: async () => {
      const { data } = await websiteContentApi.contentsProgressFilterCreate(request);
      return data;
    },
    ...options,
  });
};

export const useWebsiteContentCommentsQuery = <TData>(
  request: MonitoringWebsiteContentWebsiteContentCommonProtocolsGetCommentUserTypeFilterRequest,
  options: UseQueryOptions<MonitoringFilterProtocolResponse, unknown, TData>
) => {
  return useQuery<MonitoringFilterProtocolResponse, unknown, TData>({
    queryKey: websiteContentQueryKeys.filter.commentUserType(request),
    queryFn: async () => {
      const { data } = await websiteContentApi.contentsFilterCommentUserTypeCreate(request);
      return data;
    },
    ...options,
  });
};

export const useWebsiteContentCountQuery = (
  request: MonitoringWebsiteContentWebsiteContentCommonProtocolsGetCountRequest,
  options: UseQueryOptions<MonitoringWebsiteContentWebsiteContentCommonProtocolsGetCountResponse>
) => {
  return useQuery<MonitoringWebsiteContentWebsiteContentCommonProtocolsGetCountResponse>(
    websiteContentQueryKeys.count(request),
    async () => {
      const { data } = await websiteContentApi.contentsCountCreate(request);
      return data;
    },
    {
      ...options,
    }
  );
};

export const useWebsiteContentListQuery = <TData>(
  request: MonitoringWebsiteContentWebsiteContentCommonProtocolsGetWebsiteListRequest,
  options: UseQueryOptions<MonitoringWebsiteContentWebsiteContentCommonProtocolsGetWebsiteListResponse, unknown, TData>
) => {
  return useQuery<MonitoringWebsiteContentWebsiteContentCommonProtocolsGetWebsiteListResponse, unknown, TData>(
    websiteContentQueryKeys.list(request),
    async () => {
      const { data } = await websiteContentApi.contentsGetListCreate(request);
      return data;
    },
    {
      ...options,
    }
  );
};

export const useWebsiteContentDetailsQuery = <TData>(
  request: MonitoringWebsiteContentWebsiteContentCommonProtocolsGetWebsiteDetailRequest,
  options: UseQueryOptions<
    MonitoringWebsiteContentWebsiteContentCommonProtocolsGetWebsiteDetailResponse,
    unknown,
    TData
  >
) => {
  return useQuery<MonitoringWebsiteContentWebsiteContentCommonProtocolsGetWebsiteDetailResponse, unknown, TData>(
    websiteContentQueryKeys.details(request),
    async () => {
      const { data } = await websiteContentApi.contentsDetailsCreate(request);
      return data;
    },
    {
      ...options,
    }
  );
};

export const useWebsiteContentExportMutation = () => {
  return useMutation(websiteContentApi.contentsExcelCreate);
};

export const useWebsiteContentUpdateStatusMutation = () => {
  return useMutation(websiteContentApi.contentsStatusTabUpdateCreate, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(websiteContentQueryKeys.all());
    },
  });
};

export const useWebsiteContentTestPurchaseMutation = () => {
  return useMutation(websiteContentApi.contentsTpCreate, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(websiteContentQueryKeys.all());
    },
  });
};

export const useWebsiteContentAddAuthorizedMutation = () => {
  return useMutation(websiteContentApi.contentsAuthorizedCreate, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(websiteContentQueryKeys.all());
    },
  });
};

export const useWebsiteContentCustomTagsApplyMutation = () => {
  return useMutation(websiteContentApi.contentsCustomTagApplyCreate, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(websiteContentQueryKeys.all());
    },
  });
};
export const useWebsiteContentCustomTagsDeleteAllMutation = () => {
  return useMutation(websiteContentApi.contentsCustomTagDeleteAllCreate, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(websiteContentQueryKeys.all());
    },
  });
};
